import React, { ReactNode, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import PillIcon from './PillIcon'
import PillInput from './PillInput'
import { IPill, PillStatus, PillVariant } from './type'
import cn from 'classnames'
import { Draggable } from 'react-beautiful-dnd'
import Icon from '../Icon2'
import { Truncate, TruncateProps } from '../Truncate/Truncate'

interface PillItemProps {
  itemId: string | number
  index: number
  text: string
  variant: PillVariant
  status?: PillStatus
  background?: string
  isDisabled?: boolean
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
  isEditable?: boolean
  onChangeItem?: (data: Omit<IPill, 'id'>, index: number) => void
  onNewItemAdd?: () => void
  onRemoveItem?: (index: number) => void
  isSortable?: boolean
  stroke?: string
  onPillClickHandler?: Function
  customKeyDown?: Function
  isLastTag?: Boolean
  isSingleton?: Boolean
  pillsCount?: Number
  canTruncate?: boolean
  badgeText?: string
  highlightText?: string
  dropDownContent?: (closeDropdown: () => void) => ReactNode
  isSelected?: boolean
  isPillDisabled?: boolean
  truncateOptions?: TruncateProps
  isAiSuggested?: boolean
  isError?: boolean
  highlightAiSuggested?: boolean
  isResponsive?: boolean
}

const draggableStyleFn = (isDragging) => (isDragging ? { outline: '1px solid #6C5CE7' } : {})

const PillItem: React.FC<PillItemProps> = (props) => {

  const {
    text,
    background,
    status = "danger",
    isDisabled = false,
    variant = "label",
    leadingIcon,
    trailingIcon,
    isEditable,
    onChangeItem,
    onNewItemAdd,
    onRemoveItem,
    itemId,
    index,
    isSortable = false,
    stroke,
    onPillClickHandler,
    isSingleton,
    canTruncate,
    highlightText,
    badgeText,
    dropDownContent,
    isSelected,
    isPillDisabled,
    truncateOptions = {
      truncateFrom: 'end',
      isResponsive: true,
    },
    isAiSuggested = false,
    isError = false,
    highlightAiSuggested = false,
    isResponsive = false,
  } = props
  const truncateRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdown = useRef(null);

  const triggerRef = useRef<HTMLDivElement>(null);
  const [dropdownStyles, setDropdownStyles] = useState<React.CSSProperties>({});

  /* Update dropdown position on window resize and scroll
  and close dropdown on outside click */
  useEffect(() => {
    if (!showDropdown || !triggerRef.current) return;

    const updateDropdownPosition = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownStyles({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
      });
    };

    const handleClickOutside = (event) => {
      if (dropdown.current && !dropdown.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    updateDropdownPosition();

    window.addEventListener('resize', updateDropdownPosition);
    document.addEventListener('scroll', updateDropdownPosition, true);
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup
    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      document.removeEventListener('scroll', updateDropdownPosition, true);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handelSelfRemove = () => {
    onRemoveItem(index)
  }

  const handelOnChange = (value) => {
    if (!value) handelSelfRemove()
    if (onChangeItem && value && value !== text) onChangeItem({ text: value }, index)
  }

  const handleClick = (e) => {
    e.stopPropagation();
    const triggerEl = triggerRef.current;
  
    onPillClickHandler?.({ itemId, index, text });
    if (showDropdown) {
      setShowDropdown(false);
      return;
    }
  
    if (triggerEl) {
      const rect = triggerEl.getBoundingClientRect();
      setDropdownStyles({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
      });
    }
  
    setShowDropdown(true);
  };

  const handleOnClick = (e) => {
    handleClick(e);
  }

  const pillKeydownHandler = (event: any) => {
    if(showDropdown) return
    if (event?.key === "Enter" || (event?.key === " ")) {
      handleClick && handleClick(event)
    }
  }

  let itemStyle = undefined
  if (!(isSortable || isEditable) && (stroke || background)) {
    itemStyle = { color: '#475161' }
    if (background) itemStyle.backgroundColor = background
    if (stroke) itemStyle.outline = `1px solid ${stroke}`
  }

  // Highlight text inside a string
  function highlightInsideString(text, highlightText) {
    if (typeof text !== 'string') return '';
    if (typeof highlightText !== 'string') return text.trim();
  
    highlightText = highlightText.trim();
  
    if (!highlightText) return text.trim();
  
    // Escape special chars for RegExp
    const escaped = highlightText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');  
  
    return text.split(regex)
      .map((part, index) =>
        part.toLowerCase() === highlightText.toLowerCase()
          ? `<span class='PillItem__text-content-highlight' key="${index}">${part}</span>` 
          : part
      )
      .join('');
  }
  
  // Highlight text in truncated string
  function highlightTextInString(truncatedText, actualText, highlightText='', maxLen = 30, context = 10) {
    // Safe checks
    if (typeof truncatedText !== 'string') return '';
    if (typeof actualText !== 'string') return '';
    if (typeof highlightText !== 'string') return '';
    
    highlightText = highlightText.trim();
  
    if (!highlightText) return truncatedText.trim();
  
    // If highlight is already in view
    if (truncatedText.toLowerCase().includes(highlightText.toLowerCase())) {
      return highlightInsideString(truncatedText, highlightText);
    }
  
    // Highlight is in actual text but not in view
    const highlightIdx = actualText.toLowerCase().indexOf(highlightText.toLowerCase());
  
    if (highlightIdx === -1) return truncatedText.trim();
  
    // Calculate context window to show highlight
    let start = Math.max(0, highlightIdx - context);
    let end = Math.min(actualText.length, highlightIdx + highlightText.length + context);
  
    // If context window is greater than maxLen, trim further
    if (end - start > maxLen) {
      end = start + maxLen;
    }
  
    let rendered = '';
    if (start > 0) rendered += '…';
    rendered += actualText.slice(start, end);
    if (end < actualText.length) rendered += '…';
  
    return highlightInsideString(rendered, highlightText);
  }

  // canTruncate is false and highlightText is provided
  function highlightTextWithoutTruncation(text, highlightText) {
    if (typeof text !== 'string' || typeof highlightText !== 'string') return text;
  
    if (!highlightText || !text.toLowerCase().includes(highlightText.toLowerCase())) return text;
  
    // Escape special regex characters in highlightText
    const escaped = highlightText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const regex = new RegExp(`(${escaped})`, 'gi');
    const parts = text.split(regex);
  
    return parts.map((part, index) =>
      part.toLowerCase() === highlightText.toLowerCase() ? (
        <span key={index} className="PillItem__text-content-highlight">{part}</span>
      ) : (
        part
      )
    );
  }

  let itemClassName = cn(
    'PillItem flex-center',
    { PillItem__isDisabled: isDisabled },
    `PillItem__${variant}`,
    { [`PillItem__${status}`]: !(stroke || background || isSortable || isEditable) && status !== 'default' },
    { PillItem__interact: isSortable || isEditable },
    {
      PillItem__default:
        isSortable ||
        isEditable ||
        (status === 'default' && !stroke && !background) ||
        (!status && !stroke && !background)
    },
    { ['PillItem__link']: onPillClickHandler },
    { PillItem__selected: isSelected },
    { PillItem__isPillDisabled: isPillDisabled },
    { 'PillItem__resp_screen': isResponsive },
    { PillItem__isPillErrored: isError },
    { PillItem__isPillAiSuggested: (!isError &&isAiSuggested && highlightAiSuggested) },
  )

  useEffect(() => {
    if (truncateRef.current && canTruncate) {
      truncateRef.current.innerHTML = highlightTextInString(truncateRef.current.innerHTML,text, highlightText);
    }
  }, [text, highlightText]);

  return (
    <Draggable draggableId={String(itemId)} key={itemId} index={index} isDragDisabled={!isSortable || isDisabled}>
      {(provided, snapshot) => (
        <div
          {...provided.draggableProps}
          ref={el => {
            provided.innerRef(el);
            triggerRef.current = el;
          }}
          style={{
            ...provided.draggableProps.style, ...itemStyle, ...draggableStyleFn(snapshot.isDragging),
          }}
          className={itemClassName}
          data-test-id={`cs-pill-item-${index}`}
          onClick={handleClick}
          tabIndex={isSingleton ? 0 : null}
          onKeyDown={isSingleton ? pillKeydownHandler : null}
        >
          {isSortable ? (
            <PillIcon
              {...provided.dragHandleProps}
              role={undefined}
              aria-describedby={undefined}
              tabIndex={undefined}
              icon={<Icon icon="MoveIcon" size="extraSmall" />}
              variant={variant}
              pillIconPosition="start"
              className={`PillIcon__move ${snapshot.isDragging ? 'PillIcon__move--dragging' : ''} `}
            />
          ) : null}

          {leadingIcon ? <PillIcon icon={leadingIcon} variant={variant} pillIconPosition="start" /> : null}
          {isEditable ? (
            <PillInput
              canTruncate={canTruncate}
              className={`PillItem__inputElement ${snapshot.isDragging ? 'PillItem__inputElement--dragging' : ''}`}
              defaultValue={text}
              onChange={handelOnChange}
              onEnterPressed={onNewItemAdd}
              variant={variant}
              isDisabled={isDisabled}
              onRemoveItem={handelSelfRemove}
              isDragging={snapshot.isDragging}
              pillIndex={index}
              customKeyDown={props.customKeyDown}
              isLastTag={props.isLastTag}
              pillsCount={props.pillsCount}
            />
          ) : (
            <span
                className={`PillItem__text-content ${snapshot.isDragging ? "PillItem__text-content--dragging" : ''}${canTruncate ? ' PillItem__text-content__truncated' : ''}`}
            onClick={handleOnClick}>
            {canTruncate ? (
              <Truncate ref={truncateRef} {...truncateOptions}>
                {text}
              </Truncate>
            ) : (
              highlightTextWithoutTruncation(text, highlightText)
            )}
          </span>
          )}
          {trailingIcon ? <PillIcon icon={trailingIcon} variant={variant} pillIconPosition="end" /> : null}
          {/* Badge */}
          {badgeText ? <span className="PillItem__badge">{badgeText}</span> : null}
          {/* Dropdown content */}
          {dropDownContent && (
            <>
              <Icon
                icon={showDropdown ? "ChevronUp" : "ChevronDown"}
                size="mini"
                className="PillItem__Chevron"
              />

              {showDropdown &&
                ReactDOM.createPortal(
                  <div className="PillDropdown" style={dropdownStyles}>
                    <div
                      className="PillDropdown__content"
                      ref={dropdown}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {dropDownContent(() => setShowDropdown(false))}
                    </div>
                  </div>,
                  document.body
                )}
            </>
          )}
        </div>
      )}
    </Draggable>
  )
}

export default PillItem
